import { ShieldCheck, BarChart3, Info } from "lucide-react";
import { PageShell, PageHero } from "@/components/PageShell";
import { Reveal } from "@/components/Reveal";
import { EMAIL } from "@/lib/constants";

export default function CookiesPage() {
  return (
    <PageShell>
      <PageHero
        testid="page-cookies-hero"
        label="Vos données"
        title="Gestion des cookies"
        sub="Le détail des cookies et technologies similaires utilisés sur ce site."
      />

      <section className="bg-ivory pb-20 sm:pb-28">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <p className="pb-6 text-xs text-stone-400">Dernière mise à jour : septembre 2026</p>

          <Reveal>
            <div data-testid="cookies-notice" className="flex items-start gap-3 rounded-2xl border border-dashed border-cognac/40 bg-cognac/5 p-5 text-sm leading-relaxed text-stone-600">
              <Info className="mt-0.5 h-5 w-5 shrink-0 text-cognac" />
              <p>
                Un bandeau de consentement s'affiche lors de votre première visite pour vous permettre d'accepter ou de
                refuser la mesure d'audience. Cette page détaille, de façon transparente, les cookies et outils
                techniques réellement présents sur le site.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.05}>
            <div data-testid="cookies-necessary" className="mt-10 rounded-2xl border border-stone-200 bg-white p-6 sm:p-7">
              <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-sage">
                <ShieldCheck className="h-4 w-4" /> Cookies nécessaires
              </p>
              <h2 className="mt-3 font-display text-xl font-medium text-navy">Authentification des espaces privés</h2>
              <p className="mt-3 text-sm leading-relaxed text-stone-500 sm:text-base">
                Ces cookies sont indispensables pour vous permettre de vous connecter et de rester connecté à votre
                espace Client, Partenaire, Entreprise ou Centrale. Ils ne servent à aucune mesure d'audience ni publicité,
                et ne peuvent pas être désactivés via un bandeau : sans eux, la connexion à ces espaces est impossible.
              </p>
              <div className="mt-5 overflow-hidden rounded-xl border border-stone-200">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="bg-cream">
                      <th className="px-4 py-3 font-semibold text-navy">Cookie</th>
                      <th className="px-4 py-3 font-semibold text-navy">Rôle</th>
                      <th className="px-4 py-3 font-semibold text-navy">Durée</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100 bg-white">
                    <tr>
                      <td className="px-4 py-3 font-mono text-xs text-navy">access_token</td>
                      <td className="px-4 py-3 text-stone-500">Maintient votre session active dans un espace privé</td>
                      <td className="px-4 py-3 text-stone-500">15 minutes</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 font-mono text-xs text-navy">refresh_token</td>
                      <td className="px-4 py-3 text-stone-500">Permet de renouveler votre session sans vous reconnecter</td>
                      <td className="px-4 py-3 text-stone-500">7 jours</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.08}>
            <div data-testid="cookies-analytics" className="mt-6 rounded-2xl border border-stone-200 bg-white p-6 sm:p-7">
              <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-cognac">
                <BarChart3 className="h-4 w-4" /> Mesure d'audience / analytics
              </p>
              <h2 className="mt-3 font-display text-xl font-medium text-navy">Outil fourni par l'infrastructure technique</h2>
              <p className="mt-3 text-sm leading-relaxed text-stone-500 sm:text-base">
                Un outil de mesure d'audience (PostHog) est fourni par l'infrastructure technique sur laquelle ce site
                est hébergé (plateforme Emergent). Il peut mesurer la navigation sur le site et inclure un
                enregistrement de session (« session recording »).
              </p>
              <p className="mt-3 text-sm leading-relaxed text-stone-500 sm:text-base">
                Ce dispositif est <b className="text-navy">désactivé par défaut</b> et n'est activé que si vous cliquez sur
                « Accepter » dans le bandeau de consentement affiché à votre première visite. Vous pouvez changer d'avis
                à tout moment en effaçant les données de navigation de ce site puis en rechargeant la page pour revoir
                le bandeau.
              </p>
              <p className="mt-3 text-sm leading-relaxed text-stone-500 sm:text-base">
                Nous n'utilisons par ailleurs aucun outil Google Analytics, Google Tag Manager ou Meta/Facebook Pixel sur ce site.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <p className="mt-8 text-sm leading-relaxed text-stone-500">
              Pour toute question sur cette page, vous pouvez nous écrire à {EMAIL}. Pour plus de détails sur le traitement
              de vos données, consultez notre{" "}
              <a href="/politique-confidentialite" className="font-semibold text-cognac hover:underline">Politique de confidentialité</a>.
            </p>
          </Reveal>
        </div>
      </section>
    </PageShell>
  );
}
